/**
 * @file assets/dynamic-panel-loader.js
 * @description Generic fetch-and-inject loader for the index dynamic panels.
 * `create(config)` returns one loader that intercepts links, fetches the page
 * same-origin, extracts a fragment, caches it and injects it into a panel body
 * with loading/error states and stale-response protection, or adopts the page
 * the layout served inside the panel. The info and product panels in
 * dynamic-content.js are two instances of it. Loaded by layout/theme.liquid
 * on the index shell routes after theme-dom.js and before dynamic-content.js,
 * which reads window.DynamicPanelLoader without a fallback.
 */

(function () {
  'use strict';

  const { isDesktopViewport, normalizeWhitespace } = window.ThemeDom;

  const LOADING_TEXT = 'Loading...';
  const REDIRECT_DELAY = 1500;
  const FETCH_OPTIONS = {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' }
  };

  /**
   * Build a loader for one panel.
   *
   * The panel is `<section data-dynamic-content="<name>">` holding
   * `[<attrPrefix>-title]` and `[<attrPrefix>-body]`; inside the body the loader
   * manages `[<attrPrefix>-loader]` (loading/error line), an optional
   * `[<attrPrefix>-summary]` placeholder and `[<attrPrefix>-page-content]`
   * (the injected markup). The selected page lives in `<attrPrefix>-path` /
   * `<attrPrefix>-label` on the panel and `<attrPrefix>-state` on the body.
   *
   * config:
   *   name            panel name, used in log messages and by the router
   *   panel           the panel element (null when the template lacks it)
   *   attrPrefix      e.g. 'data-dynamic-info'
   *   defaultLabel    title when nothing is selected or the link has no text
   *   loaderClass / errorClass / contentClass
   *                   class names for the loader line and the content container
   *   errorText       loader line text after a failed fetch
   *   cacheLimit      max cached pages (0 = unbounded); oldest is evicted
   *   linkSelector    `closest()` selector for links this loader may claim
   *   resolveLink(link, path)
   *                   null to decline, or { path, redirectPath?, label? }
   *   extract(doc, label)
   *                   { html, title? } from the fetched document, or null; the
   *                   loader adds the document's <title> as documentTitle
   *   onInject(container, label)
   *                   optional; re-initialise behaviours inside freshly
   *                   injected markup. `label` is the panel title shown for it.
   *   isActive()      true while the router shows this panel (guards the
   *                   fallback redirect after a failed fetch)
   *
   * Every `load` bumps a private fetch id; a response, extraction failure or
   * redirect that no longer matches the id is ignored, so `cancel()` (on close,
   * on leaving the desktop viewport) makes stale work inert.
   */
  function create(config) {
    const {
      name,
      panel,
      attrPrefix,
      defaultLabel,
      loaderClass,
      errorClass,
      contentClass,
      errorText,
      cacheLimit,
      linkSelector,
      resolveLink,
      extract,
      onInject,
      isActive
    } = config;
    const body = panel ? panel.querySelector(`[${attrPrefix}-body]`) : null;
    const titleElement = panel ? panel.querySelector(`[${attrPrefix}-title]`) : null;
    const placeholder = body ? body.querySelector(`[${attrPrefix}-summary]`) : null;
    const pathAttr = `${attrPrefix}-path`;
    const labelAttr = `${attrPrefix}-label`;
    const stateAttr = `${attrPrefix}-state`;
    const loaderAttr = `${attrPrefix}-loader`;
    const contentAttr = `${attrPrefix}-page-content`;
    const cache = new Map();
    // The loader line ships with the product shell and is created on first use
    // for the info shell; once present it is only ever hidden. The content
    // container is created on the first injection and removed with its markup.
    let loaderElement = body ? body.querySelector(`[${loaderAttr}]`) : null;
    let contentContainer = body ? body.querySelector(`[${contentAttr}]`) : null;
    let currentLabel = defaultLabel;
    let fetchId = 0;
    // The current selection ({ path, redirectPath, label }) and whether a
    // fetch for it is still in flight, so ensureLoaded() can resume a load
    // that cancel() abandoned without doubling one that is running.
    let selection = null;
    let loading = false;
    // Path of the page the layout served inside the panel (adopt()); its cache
    // entry is the page's own markup and survives clearCache().
    let adoptedPath = '';
    // The <title> of the page the panel shows, kept with the shown markup rather
    // than looked up in the cache, so clearCache() on a back/forward-cache
    // return cannot take the title of a page still on screen (L-53).
    let shownDocumentTitle = '';

    const loader = {
      name,
      panel,
      canUse,
      intercept,
      select,
      adopt,
      restore,
      getPath,
      getLabel,
      getDocumentTitle,
      ensureLoaded,
      reset,
      cancel,
      clearCache
    };

    /** The panel is a desktop-only presentation. */
    function canUse() {
      return Boolean(panel && isDesktopViewport());
    }

    /**
     * Details for a click this loader should handle, or null. The label falls
     * back to the link text, its aria-label and finally the panel default.
     */
    function intercept(event) {
      const candidate = extractInterceptableLink(event, linkSelector);
      const resolved = candidate ? resolveLink(candidate.link, candidate.path) : null;

      if (!resolved) {
        return null;
      }

      const { link } = candidate;
      const label = normalizeWhitespace(resolved.label)
        || normalizeWhitespace(link.textContent)
        || normalizeWhitespace(link.getAttribute('aria-label'))
        || defaultLabel;

      return {
        loader,
        link,
        path: normalizePathname(resolved.path),
        redirectPath: normalizePathname(resolved.redirectPath || resolved.path),
        label
      };
    }

    /** Record the selection on the panel and start loading it. */
    function select(details) {
      if (!panel) {
        return;
      }

      selection = {
        path: details.path,
        redirectPath: details.redirectPath,
        label: details.label
      };
      panel.setAttribute(pathAttr, details.path);
      setLabel(details.label);
      load(selection);
    }

    /**
     * Take over the page the layout served inside the panel (a product, page,
     * policy or cart route loaded at its own URL inside the index shell): the
     * served markup becomes the selection for `path` and its cache entry, so
     * getDocumentTitle() reports the page's title, restore() finds it loaded
     * and a later selection of the same path needs no fetch. The label falls
     * back to the one the layout wrote on the panel. Returns false when the
     * panel carries no served content.
     */
    function adopt(details) {
      if (!panel || !body || !contentContainer) {
        return false;
      }

      const path = normalizePathname(details.path);
      const label = normalizeWhitespace(details.label) || panel.getAttribute(labelAttr) || defaultLabel;

      cancel();
      selection = { path, redirectPath: path, label };
      adoptedPath = path;
      panel.setAttribute(pathAttr, path);
      setLabel(label);
      body.setAttribute(stateAttr, 'loaded');
      hidePlaceholder();

      if (loaderElement) {
        loaderElement.hidden = true;
      }

      shownDocumentTitle = normalizeWhitespace(document.title);
      cache.set(path, {
        html: contentContainer.innerHTML,
        title: label,
        documentTitle: shownDocumentTitle
      });

      return true;
    }

    /**
     * Bring the panel back to a recorded selection ({ path, label }) when a
     * history entry is revisited: the same page is re-issued only if its
     * fetch was cancelled or failed; a different page is selected afresh.
     */
    function restore(details) {
      if (!panel) {
        return;
      }

      if (getPath() === details.path) {
        ensureLoaded();
        return;
      }

      select({ path: details.path, redirectPath: details.path, label: details.label });
    }

    function getPath() {
      return panel ? panel.getAttribute(pathAttr) || '' : '';
    }

    function getLabel() {
      return currentLabel;
    }

    /** The <title> of the page shown, or '' while nothing is loaded. */
    function getDocumentTitle() {
      return shownDocumentTitle;
    }

    /**
     * Re-issue the selection's load when the panel is shown again after a
     * cancelled or failed fetch (e.g. Forward to a product entry after closing
     * it mid-load). A running fetch or already loaded content is left alone.
     */
    function ensureLoaded() {
      if (selection && body && !loading && body.getAttribute(stateAttr) !== 'loaded') {
        load(selection);
      }
    }

    function cancel() {
      fetchId += 1;
      loading = false;
    }

    /**
     * Drop fetched pages; the served page (adopt) is the document's own markup
     * and stays. The page on screen keeps its title (getDocumentTitle).
     */
    function clearCache() {
      const adopted = adoptedPath ? cache.get(adoptedPath) : null;

      cache.clear();

      if (adopted) {
        cache.set(adoptedPath, adopted);
      }
    }

    /** Forget the selection and return the panel to its empty state. */
    function reset() {
      cancel();
      selection = null;

      if (!panel) {
        return;
      }

      panel.removeAttribute(pathAttr);
      panel.removeAttribute(labelAttr);
      setLabel(defaultLabel);

      if (!body) {
        return;
      }

      body.removeAttribute(stateAttr);
      hidePlaceholder();
      removeContent();

      if (loaderElement) {
        loaderElement.hidden = true;
        loaderElement.textContent = LOADING_TEXT;
      }
    }

    function load(details) {
      if (!body) {
        return;
      }

      const { path, redirectPath, label } = details;
      const requestId = ++fetchId;

      // Any earlier request is superseded by this id.
      loading = false;

      if (cache.has(path)) {
        setContent(cache.get(path));
        return;
      }

      setLoaderState('loading', LOADING_TEXT, loaderClass);
      loading = true;

      fetch(path, FETCH_OPTIONS)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          return response.text();
        })
        .then((html) => {
          if (requestId !== fetchId) {
            return;
          }

          loading = false;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const extracted = extract(doc, label);

          if (!extracted || !extracted.html) {
            throw new Error(`Could not extract ${name} content`);
          }

          extracted.documentTitle = normalizeWhitespace(doc.title);

          if (cacheLimit && cache.size >= cacheLimit) {
            cache.delete(cache.keys().next().value);
          }

          cache.set(path, extracted);
          setContent(extracted);
        })
        .catch((error) => {
          if (requestId !== fetchId) {
            return;
          }

          loading = false;
          console.warn(`Dynamic ${name} panel: fetch failed for ${path}`, error);
          setLoaderState('error', errorText, errorClass);

          // Give the user a moment to read the error, then navigate for real.
          window.setTimeout(() => {
            if (fetchId === requestId && isActive()) {
              window.location.href = redirectPath;
            }
          }, REDIRECT_DELAY);
        });
    }

    function setLabel(label) {
      currentLabel = normalizeWhitespace(label) || defaultLabel;
      panel.setAttribute(labelAttr, currentLabel);

      if (titleElement) {
        titleElement.textContent = currentLabel;
      }
    }

    /** Show the loader line in place of any injected content. */
    function setLoaderState(state, text, className) {
      body.setAttribute(stateAttr, state);
      hidePlaceholder();
      removeContent();

      if (!loaderElement) {
        loaderElement = document.createElement('p');
        loaderElement.setAttribute(loaderAttr, '');
        body.appendChild(loaderElement);
      }

      loaderElement.className = className;
      loaderElement.hidden = false;
      loaderElement.textContent = text;
    }

    function setContent(content) {
      body.setAttribute(stateAttr, 'loaded');

      if (content.title) {
        setLabel(content.title);
      }

      if (loaderElement) {
        loaderElement.hidden = true;
      }

      hidePlaceholder();

      if (!contentContainer) {
        contentContainer = document.createElement('div');
        contentContainer.setAttribute(contentAttr, '');
        contentContainer.className = contentClass;
        body.appendChild(contentContainer);
      }

      contentContainer.innerHTML = content.html;
      shownDocumentTitle = content.documentTitle || '';

      if (typeof onInject === 'function') {
        onInject(contentContainer, currentLabel);
      }
    }

    function removeContent() {
      shownDocumentTitle = '';

      if (contentContainer) {
        contentContainer.remove();
        contentContainer = null;
      }
    }

    function hidePlaceholder() {
      if (placeholder) {
        placeholder.hidden = true;
      }
    }

    return loader;
  }

  /**
   * The same-origin link behind a click that matches `selector` and is safe to
   * intercept (no download, no target, no mailto/tel/javascript, not a
   * fragment): `{ link, path }` with the normalised pathname, or null.
   */
  function extractInterceptableLink(event, selector) {
    if (!event || !(event.target instanceof Element)) {
      return null;
    }

    const link = event.target.closest(selector);

    if (!link || link.hasAttribute('download')) {
      return null;
    }

    const targetValue = String(link.getAttribute('target') || '').trim().toLowerCase();

    if (targetValue && targetValue !== '_self') {
      return null;
    }

    const hrefValue = String(link.getAttribute('href') || '').trim();

    if (!hrefValue || hrefValue.startsWith('#') || /^(mailto|tel|javascript):/i.test(hrefValue)) {
      return null;
    }

    let resolvedUrl;

    try {
      resolvedUrl = new URL(hrefValue, window.location.origin);
    } catch (error) {
      return null;
    }

    if (resolvedUrl.origin !== window.location.origin) {
      return null;
    }

    return {
      link,
      path: normalizePathname(resolvedUrl.pathname)
    };
  }

  /**
   * First element under `root` matching one of `selectors`, in order, that has
   * content and passes the optional `accept` test.
   */
  function findContentElement(root, selectors, accept) {
    for (const selector of selectors) {
      const match = root.querySelector(selector);

      if (match && match.innerHTML.trim().length > 0 && (!accept || accept(match))) {
        return match;
      }
    }

    return null;
  }

  /** Lower-case, leading slash, no duplicate or trailing slashes. */
  function normalizePathname(pathname) {
    const rawPathname = String(pathname || '').trim();

    if (!rawPathname) {
      return '/';
    }

    let normalizedPathname = rawPathname.toLowerCase();

    if (!normalizedPathname.startsWith('/')) {
      normalizedPathname = `/${normalizedPathname}`;
    }

    normalizedPathname = normalizedPathname.replace(/\/{2,}/g, '/');

    if (normalizedPathname.length > 1) {
      normalizedPathname = normalizedPathname.replace(/\/+$/, '');
    }

    return normalizedPathname || '/';
  }

  window.DynamicPanelLoader = {
    create,
    findContentElement,
    normalizePathname
  };
})();
